import { Group, Tabs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Measure } from '../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/page-layout/page-layout';
import { AuditLogSidebarProvider, useAuditLogSidebar } from '../../contexts/audit-log-sidebar.context';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useWorkPeriodStore } from '../../store/work-period-store/work-period-store';
import { useWorkPeriodContextReset } from '../../store/work-period-store/work-period-store.hooks';
import { baseWhite, inkLight, secondaryBase } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { H2 } from '../../ui-library/typography/typography';
import { StyledTabs } from '../adherence/adherence';
import { useFlora } from '../flora/flora.context';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import type { TaskRecord } from '../tasks-table/tasks-table.type';
import { AssessmentView } from './assessment-view/assessment-view';
import { AuditLogSidebar } from './assessment-view/sections/audit-log/audit-log-sidebar';
import { Breakdown } from './breakdown/breakdown';
import { ComparisonView } from './comparison-view/comparison-view';
import { WorkPeriodTabs } from './components/work-period-tabs';
import { WorkPeriodsUnifiedFilters } from './components/work-periods-unified-filters';
import { DeliveryConfidence } from './delivery-confidence/delivery-confidence';
import { TaskDetails } from './task-detail/task-details';
import { TaskSideSection } from './task-detail/task-side-section';
import { WorkPace } from './work-pace/work-pace';
import { getWorkPeriodsFloraContext } from './work-periods.helpers';
import { WorkPeriodTabType, WorkPeriodType, WorkPeriodsTab } from './work-periods.type';
import { WorkPhaseDistribution } from './work-phase-distribution/work-phase-distribution';

function WorkPeriodsContent({
  expandTaskDetails,
  toggleExpandTaskDetails,
}: {
  expandTaskDetails: boolean;
  toggleExpandTaskDetails: () => void;
}) {
  const i18n = useTranslation();
  // Reset work period selections when organization/portfolio changes
  useWorkPeriodContextReset();

  // Use the new work period store
  const { selectedTab, setSelectedTab, selectedWorkPeriodType } = useWorkPeriodStore();

  const { isAuditLogSidebarVisible } = useAuditLogSidebar();

  // Filter panel state
  const [isFilterPanelOpen, { toggle: toggleFilterPanel }] = useDisclosure(true);

  // Side section state
  const [showSideSection, setShowSideSection] = useState(false);
  const [sideSectionTasks, setSideSectionTasks] = useState<TaskRecord[]>([]);
  const [sideSectionTitle, setSideSectionTitle] = useState('');

  // Comparison view specific state
  const [selectedMeasures, setSelectedMeasures] = useState<string[]>([]);
  const [selectedMeasure, setSelectedMeasure] = useState<string>('');
  const [showTrendLine, setShowTrendLine] = useState<boolean>(false);
  const [showDots, setShowDots] = useState<boolean>(true);
  const [isTasksFetching, setIsTasksFetching] = useState<boolean>(false);

  // Remove data fetching and loading state - will be handled per tab
  const isPreDefined = selectedWorkPeriodType === WorkPeriodType.Defined;

  useEffect(() => {
    if (!isPreDefined) {
      setSelectedTab(WorkPeriodsTab.ASSESSMENT);
    }
  }, [isPreDefined, setSelectedTab]);

  const handleMeasuresChange = useCallback((measures: string[]) => {
    setSelectedMeasures(measures);
  }, []);

  const handleMeasureChange = useCallback((measure: string | null) => {
    if (measure) {
      setSelectedMeasure(measure);
    }
  }, []);

  const handleShowTrendLineChange = useCallback((value: boolean) => {
    setShowTrendLine(value);
  }, []);

  const handleShowDotsChange = useCallback((value: boolean) => {
    setShowDots(value);
  }, []);

  const handleSideSectionTasksChange = useCallback(
    (tasks: TaskRecord[], title?: string, isLoading?: boolean) => {
      setShowSideSection(true);
      setSideSectionTitle(title || i18n.t('common.work_units'));
      setSideSectionTasks(tasks);
      setIsTasksFetching(isLoading || false);
    },
    [i18n.t],
  );

  const handleCloseSideSection = useCallback(() => {
    setShowSideSection(false);
    setSideSectionTasks([]);
    setSideSectionTitle('');
  }, []);

  const handleSetSelectedTab = (tab: WorkPeriodsTab) => {
    handleCloseSideSection();
    setSelectedTab(tab);
  };

  const sideFiltersContent = useMemo(() => {
    const isComparisonView =
      selectedTab === WorkPeriodsTab.COMPARISON_TABLE ||
      selectedTab === WorkPeriodsTab.COMPARISON_MEASURE_COMPARISON ||
      selectedTab === WorkPeriodsTab.COMPARISON_MEASURES_OVER_TIME;

    return (
      <WorkPeriodsUnifiedFilters
        isComparisonView={isComparisonView}
        activeTab={isComparisonView ? selectedTab : undefined}
        customMeasures={[]} // Will be handled by individual tabs
        selectedMeasures={selectedMeasures}
        onMeasuresChange={handleMeasuresChange}
        selectedMeasure={selectedMeasure}
        onMeasureChange={handleMeasureChange}
        showTrendLine={showTrendLine}
        onShowTrendLineChange={handleShowTrendLineChange}
        showDots={showDots}
        onShowDotsChange={handleShowDotsChange}
      />
    );
  }, [
    selectedTab,
    selectedMeasures,
    handleMeasuresChange,
    selectedMeasure,
    handleMeasureChange,
    showTrendLine,
    handleShowTrendLineChange,
    showDots,
    handleShowDotsChange,
  ]);

  const sideFiltersConfig = useMemo(
    () => ({
      content: sideFiltersContent,
      show: isFilterPanelOpen,
      setShow: toggleFilterPanel,
    }),
    [sideFiltersContent, isFilterPanelOpen, toggleFilterPanel],
  );

  const mainSection = useMemo(
    () => (
      <>
        {(() => {
          switch (selectedTab) {
            case WorkPeriodsTab.ASSESSMENT:
              return <AssessmentView onTasksChange={handleSideSectionTasksChange} tasksOpen={showSideSection} />;
            case WorkPeriodsTab.WORK_PHASE_DISTRIBUTION:
              return <WorkPhaseDistribution onTasksChange={handleSideSectionTasksChange} tasksOpen={showSideSection} />;
            case WorkPeriodsTab.COMPARISON_TABLE:
            case WorkPeriodsTab.COMPARISON_MEASURE_COMPARISON:
            case WorkPeriodsTab.COMPARISON_MEASURES_OVER_TIME:
              return <ComparisonView onTasksChange={handleSideSectionTasksChange} />;
            case WorkPeriodsTab.BREAKDOWN:
              return <Breakdown />;
            case WorkPeriodsTab.WORK_PACE:
              return <WorkPace onTasksChange={handleSideSectionTasksChange} tasksOpen={showSideSection} />;
            case WorkPeriodsTab.DELIVERY_CONFIDENCE:
              return <DeliveryConfidence onTasksChange={handleSideSectionTasksChange} />;
            default:
              return null;
          }
        })()}
      </>
    ),
    [selectedTab, handleSideSectionTasksChange, showSideSection],
  );

  const sideSection = useMemo(() => {
    if (isAuditLogSidebarVisible) {
      return <AuditLogSidebar />;
    }
    if (!showSideSection) return null;
    return (
      <TaskSideSection
        key={sideSectionTitle}
        categoryTitle={sideSectionTitle}
        tasks={sideSectionTasks}
        onClose={handleCloseSideSection}
        isLoading={isTasksFetching}
        onExpandTaskDetails={toggleExpandTaskDetails}
      />
    );
  }, [
    showSideSection,
    sideSectionTitle,
    sideSectionTasks,
    isAuditLogSidebarVisible,
    handleCloseSideSection,
    isTasksFetching,
    toggleExpandTaskDetails,
  ]);

  return (
    <>
      {expandTaskDetails ? (
        <TaskDetails
          tasks={sideSectionTasks as TaskRecord[]}
          title={`(${sideSectionTasks.length}) ${sideSectionTitle}`}
          onBack={() => toggleExpandTaskDetails()}
        />
      ) : (
        <PageLayout
          controlBar={undefined}
          tabs={<WorkPeriodTabs selectedTab={selectedTab} setSelectedTab={handleSetSelectedTab} />}
          sideFilters={sideFiltersConfig}
          mainSection={mainSection}
          sideSection={sideSection}
          showSideSection={showSideSection || isAuditLogSidebarVisible}
        />
      )}
    </>
  );
}

export function WorkPeriods() {
  const i18n = useTranslation();
  useDocumentTitle('Work Periods - Bloomfilter');

  const {
    selectedTab,
    setSelectedTab,
    selectedBoardId,
    selectedWorkPeriods,
    selectedWorkPeriodType,
    dateRange,
    selectedMeasuresForComparison,
    selectedMeasureForOverTime,
    clearWorkPeriods,
  } = useWorkPeriodStore();
  const { setContextualInfo } = useFlora();
  const teams = useGlobalStore((state) => state.teams);

  const [expandTaskDetails, { toggle: toggleExpandTaskDetails }] = useDisclosure(false);

  // One-time URL parameter initialization for selectedTab (from Flora quick links)
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitTabFromUrlRef = useRef(false);

  useEffect(() => {
    if (didInitTabFromUrlRef.current) return;

    const urlSelectedTab = searchParams.get('selectedTab');

    if (!urlSelectedTab) {
      didInitTabFromUrlRef.current = true;
      return;
    }

    if (Object.values(WorkPeriodsTab).includes(urlSelectedTab as WorkPeriodsTab)) {
      setSelectedTab(urlSelectedTab as WorkPeriodsTab);
    }

    didInitTabFromUrlRef.current = true;

    // Only clear selectedTab from URL params — leave board/date params for the filter component
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('selectedTab');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, setSelectedTab]);

  useEffect(() => {
    // Determine which measures to send based on the selected tab
    let selectedMeasures: string[] = [];

    switch (selectedTab) {
      case WorkPeriodsTab.WORK_PHASE_DISTRIBUTION:
        selectedMeasures = [Measure.FlowByPhase];
        break;
      case WorkPeriodsTab.WORK_PACE:
      case WorkPeriodsTab.DELIVERY_CONFIDENCE:
        selectedMeasures = [Measure.FlowByPace];
        break;
      case WorkPeriodsTab.COMPARISON_MEASURE_COMPARISON:
        selectedMeasures = selectedMeasuresForComparison.map((m) => m.measure_name);
        break;
      case WorkPeriodsTab.COMPARISON_MEASURES_OVER_TIME:
        selectedMeasures = selectedMeasureForOverTime ? [selectedMeasureForOverTime.measure_name] : [];
        break;
    }

    setContextualInfo(
      getWorkPeriodsFloraContext(
        selectedBoardId,
        selectedWorkPeriods,
        dateRange,
        teams,
        selectedWorkPeriodType,
        selectedMeasures,
      ),
    );
  }, [
    selectedBoardId,
    selectedWorkPeriods,
    selectedWorkPeriodType,
    dateRange,
    teams,
    selectedTab,
    selectedMeasuresForComparison,
    selectedMeasureForOverTime,
    setContextualInfo,
  ]);

  const isComparisonView =
    selectedTab === WorkPeriodsTab.COMPARISON_TABLE ||
    selectedTab === WorkPeriodsTab.COMPARISON_MEASURE_COMPARISON ||
    selectedTab === WorkPeriodsTab.COMPARISON_MEASURES_OVER_TIME;

  return (
    <AuditLogSidebarProvider>
      <MenuDrivenContainer
        header={
          <Group style={{ minWidth: 550 }}>
            <Icon name="calendar_month" color={secondaryBase} size={36} />
            <H2>{`Work Periods${expandTaskDetails ? ` ${i18n.t('common.work_units')}` : ''}`}</H2>
            {!expandTaskDetails && (
              <StyledTabs
                variant="pills"
                radius="xl"
                value={isComparisonView ? WorkPeriodTabType.COMPARISON : WorkPeriodTabType.SINGLE}
              >
                <Tabs.List>
                  <Tabs.Tab
                    value={WorkPeriodTabType.SINGLE}
                    leftSection={<Icon name="ads_click" size={16} color={isComparisonView ? inkLight : baseWhite} />}
                    onClick={() => {
                      if (isComparisonView) {
                        clearWorkPeriods();
                        setSelectedTab(WorkPeriodsTab.ASSESSMENT);
                      }
                    }}
                  >
                    Assessment
                  </Tabs.Tab>
                  <Tabs.Tab
                    value={WorkPeriodTabType.COMPARISON}
                    leftSection={
                      <Icon name="compare_arrows" size={16} color={isComparisonView ? baseWhite : inkLight} />
                    }
                    onClick={() => {
                      if (!isComparisonView) {
                        setSelectedTab(WorkPeriodsTab.COMPARISON_TABLE);
                      }
                    }}
                  >
                    Comparison
                  </Tabs.Tab>
                </Tabs.List>
              </StyledTabs>
            )}
          </Group>
        }
      >
        <WorkPeriodsContent expandTaskDetails={expandTaskDetails} toggleExpandTaskDetails={toggleExpandTaskDetails} />
      </MenuDrivenContainer>
    </AuditLogSidebarProvider>
  );
}
